/**
 * Brand Brain editor model — the PURE form layer behind Admin → Company
 * Brain.
 *
 * The operator edits four canonical org-level brand fields (voice & tone,
 * positioning, key offers, free-form brand-markdown notes). Each field maps
 * to ONE deterministic BrandEntry id, so saving always upserts the same
 * records instead of accreting duplicates, and reloading the editor is a
 * simple read-back by id. No IO, no store, no clock — callers supply
 * `now`/`updatedBy` and persist the returned BrandBrain themselves (see
 * ./persist.ts), which keeps every decision here unit-testable.
 */

import type { BrandBrain, BrandEntry, BrandEntryKind } from './schema';
import { archiveBrandEntry, upsertBrandEntry } from './store';

// ---------------------------------------------------------------------------
// Field catalog
// ---------------------------------------------------------------------------

/** The four org-level brand fields the Admin editor exposes. */
export type BrandFieldKey = 'voice' | 'positioning' | 'offers' | 'notes';

export interface BrandFieldMeta {
  key: BrandFieldKey;
  /** Deterministic entry id — the same field always upserts the same record. */
  id: string;
  kind: BrandEntryKind;
  label: string;
  /** Operator-facing hint under the field label. */
  hint: string;
  /** Prompt-composition priority (weight desc wins the token budget). */
  weight: number;
}

/** Canonical order + copy; the editor renders fields in this order. */
export const BRAND_FIELDS: readonly BrandFieldMeta[] = [
  {
    key: 'voice',
    id: 'brand-voice',
    kind: 'voice',
    label: 'Voice & tone',
    hint: 'How the brand sounds — register, rhythm, do/don\u2019t rules. e.g. "Plainspoken, confident, zero hype. No exclamation marks."',
    weight: 5,
  },
  {
    key: 'positioning',
    id: 'brand-positioning',
    kind: 'positioning',
    label: 'Positioning',
    hint: 'Value prop, who it\u2019s for, and what makes it different from the field.',
    weight: 4,
  },
  {
    key: 'offers',
    id: 'brand-offers',
    kind: 'offer',
    label: 'Key offers',
    hint: 'The concrete things you sell — packages, outcomes, price anchors.',
    weight: 3,
  },
  {
    key: 'notes',
    id: 'brand-notes',
    kind: 'directive',
    label: 'Brand notes (markdown)',
    hint: 'Free-form brand knowledge — proof points, banned phrases, anything else the AI must respect. Markdown welcome.',
    weight: 2,
  },
] as const;

const META_BY_KEY: Record<BrandFieldKey, BrandFieldMeta> = Object.fromEntries(
  BRAND_FIELDS.map((f) => [f.key, f]),
) as Record<BrandFieldKey, BrandFieldMeta>;

export function brandFieldMeta(key: BrandFieldKey): BrandFieldMeta {
  return META_BY_KEY[key];
}

/** The editor's working value: one string per field. */
export type BrandFieldValues = Record<BrandFieldKey, string>;

export const emptyBrandFieldValues: BrandFieldValues = {
  voice: '',
  positioning: '',
  offers: '',
  notes: '',
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Per-field character ceiling — keeps prompt fragments budget-friendly. */
export const BRAND_FIELD_MAX = 4000;

export interface BrandInputIssue {
  /** 'form' = cross-field issue (nothing filled in). */
  field: BrandFieldKey | 'form';
  message: string;
}

export interface BrandInputValidation {
  ok: boolean;
  issues: BrandInputIssue[];
}

/**
 * Validate the whole form. Rules:
 *   - at least ONE field must be non-blank (an all-empty save is a no-op
 *     the operator should be told about, not a silent success);
 *   - every field must fit inside BRAND_FIELD_MAX after trimming.
 * Total + pure: never throws, same input → same output.
 */
export function validateBrandInput(values: BrandFieldValues): BrandInputValidation {
  const issues: BrandInputIssue[] = [];
  const anySet = BRAND_FIELDS.some((f) => (values[f.key] ?? '').trim().length > 0);
  if (!anySet) {
    issues.push({
      field: 'form',
      message: 'Fill in at least one field — an empty Brand Brain teaches the AI nothing.',
    });
  }
  for (const f of BRAND_FIELDS) {
    const len = (values[f.key] ?? '').trim().length;
    if (len > BRAND_FIELD_MAX) {
      issues.push({
        field: f.key,
        message: `${f.label} is ${len - BRAND_FIELD_MAX} characters over the ${BRAND_FIELD_MAX}-character limit.`,
      });
    }
  }
  return { ok: issues.length === 0, issues };
}

// ---------------------------------------------------------------------------
// Form value → BrandEntry
// ---------------------------------------------------------------------------

export interface ToBrandRecordOptions {
  /** Operator name / email stamped on the entry. */
  updatedBy: string;
  /** ISO-8601 UTC timestamp — injected so tests are deterministic. */
  now: string;
}

/**
 * Convert one field's text into its canonical BrandEntry: deterministic id,
 * field-mapped kind/weight, trimmed body, 'all' scopes (org-level brand
 * knowledge routes to every output surface), active status.
 */
export function toBrandRecord(
  key: BrandFieldKey,
  text: string,
  opts: ToBrandRecordOptions,
): BrandEntry {
  const meta = brandFieldMeta(key);
  return {
    id: meta.id,
    kind: meta.kind,
    title: meta.label,
    body: text.trim(),
    weight: meta.weight,
    scopes: ['all'],
    status: 'active',
    updatedBy: opts.updatedBy.trim() || 'operator',
    updatedAt: opts.now,
  };
}

/**
 * Apply the whole form to a BrandBrain. Non-blank fields upsert their
 * canonical entry; a field the operator CLEARED archives its entry (soft
 * delete — history stays intact) when one exists. Entries outside the
 * four canonical ids (agent-learned lexicon, proofs, …) are untouched.
 * IMMUTABLE: returns a new BrandBrain.
 */
export function applyBrandFields(
  brain: BrandBrain,
  values: BrandFieldValues,
  opts: ToBrandRecordOptions,
): BrandBrain {
  let next = brain;
  for (const f of BRAND_FIELDS) {
    const text = (values[f.key] ?? '').trim();
    if (text) {
      next = upsertBrandEntry(next, toBrandRecord(f.key, text, opts));
    } else if (next.entries.some((e) => e.id === f.id && e.status !== 'archived')) {
      next = archiveBrandEntry(next, f.id);
    }
  }
  return next;
}

/**
 * Read the editor's form values back out of a BrandBrain — the reload path.
 * Archived canonical entries read as '' (the operator cleared them).
 */
export function fieldsFromBrain(brain: BrandBrain): BrandFieldValues {
  const values: BrandFieldValues = { ...emptyBrandFieldValues };
  for (const f of BRAND_FIELDS) {
    const entry = brain.entries.find((e) => e.id === f.id);
    values[f.key] = entry && entry.status !== 'archived' ? entry.body : '';
  }
  return values;
}

/** Which canonical fields are currently set — drives the coverage chips. */
export function setBrandFieldKeys(brain: BrandBrain): BrandFieldKey[] {
  const values = fieldsFromBrain(brain);
  return BRAND_FIELDS.filter((f) => values[f.key].trim().length > 0).map((f) => f.key);
}
